/**
 * Sanitize marked HTML output before it lands in dangerouslySetInnerHTML.
 *
 * Why: marked v17's default config passes raw HTML through to output —
 * `<script>`, `javascript:` hrefs, and inline event handlers (`onerror`,
 * `onclick`, …) all survive. Confirmed in the regression test at
 * `__tests__/markdown-sanitization.test.ts`. The same flow is reachable
 * on every surface that renders a `.md` file:
 *
 *   - share-view single-file preview (recipient session)
 *   - drive preview of an owner's `.md`
 *   - cross-tenant mounted `.md` from another org (post-PR-#149,
 *     same threat shape as share-view of cross-tenant content —
 *     attacker is the source-tenant owner, victim is the recipient
 *     who has full papan.ai cookies in scope)
 *   - agent chat message preview (LLM output may emit script tags
 *     under prompt injection)
 *
 * DOMPurify's default config strips: `<script>`, `<iframe>`, all `on*`
 * handlers, `javascript:` / `data:` URIs in attributes (with a few
 * explicit allowances), and any other element/attribute not on its
 * built-in safelist. Markdown-output tags (h1-h6, p, code, pre, a,
 * blockquote, ul/ol/li, table, img, hr, …) survive intact.
 *
 * Used by MarkdownViewer + MarkdownEditor + agent/MarkdownContent.
 */
import DOMPurify from 'dompurify'

/**
 * Sanitize an HTML string for safe injection via dangerouslySetInnerHTML.
 *
 * **Fails closed when no DOM is available.** DOMPurify needs a DOM parser
 * to operate. Today every consumer renders client-side after hydration,
 * but if a future SSR path ever calls this, returning the input verbatim
 * would silently ship XSS to the client. Returning empty string instead
 * makes the SSR failure visible (blank preview) rather than dangerous.
 */
export function sanitizeMarkdownHtml(html: string): string {
  if (typeof window === 'undefined') return ''
  // DOMPurify's default config already covers the markdown-output tags
  // we need (h1-h6, p, code, pre, a, blockquote, ul/ol/li, table, img,
  // hr, plus the `class` attribute on `<pre>` for mermaid pre-render
  // wrappers) AND strips script/iframe/event handlers/javascript: URIs.
  // USE_PROFILES.html was tried — it swaps the default safelist for an
  // HTML-profile safelist that drops markdown tags (<p> went missing,
  // <script> stayed) — wrong shape for our use case.
  return DOMPurify.sanitize(html)
}
